import WalletSelect from "@/features/wallet-select";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import MarketLimitButtons from "./components/market-limit-buttons";
import ConfirmButton from "./components/confirm-button";
import { useToken } from "../../hooks/use-token";
import {
  BotWalletSupportedChain,
  useWallet,
  WalletType,
} from "@/providers/wallet";
import BalanceTokenInput from "./components/balance-token-input";
import { useSellBotActions } from "./hooks/use-sell-bot-hook";
import { useBotSellTokenMutation } from "@/api/bot/bot-sell-token";
import { useBotLimitSellMutation } from "@/api/bot/bot-limit-sell";
import YouReceiveTokenUsdInput from "./components/you-receive-token-usd-input";
import { useBuySellBotTokenContext } from "../../providers/buy-sell-token-provider";
import { formatNativeCurrency, formatTokenCurrency } from "@/utils/currency";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";

import { useQueryClient } from "@tanstack/react-query";
import LimitInput from "./components/limit-input";
import SlippageToggleInput from "./slippage-toggle-input";
import { useEffect, useState } from "react";
import { BOT_GET_ORDERS_QUERY_KEY } from "@/api/bot/bot-get-orders";
import { BOT_GET_TOKEN_INFO_QUERY_KEY } from "@/api/bot/bot-get-token-info";
import { BOT_GET_USER_INFO_QUERY_KEY } from "@/api/bot/bot-get-user-info";
import { POSITIONS_QUERY_KEY } from "@/api/positions/positions-bot";
import { TRANSACTIONS_BOT_QUERY_KEY } from "@/api/transactions/transactions-bot";

const SellBotActions = () => {
  const token = useToken();
  const { handlers, state } = useSellBotActions();

  const { userId, appPubKey, currentWallet: wallet } = useWallet();
  const { mutate, isPending, isSuccess } = useBotLimitSellMutation();
  const {
    mutate: mutateInstant,
    isPending: isPendingInstant,
    isSuccess: isSuccessInstant,
  } = useBotSellTokenMutation();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    sellSlippage,
    isSellSlippageAuto,
    setIsSellSlippageAuto,
    setSellSlippage,
  } = useBuySellBotTokenContext();
  const { isConnected } = useWeb3Auth();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (isSuccess || isSuccessInstant) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
  }, [isSuccess, isSuccessInstant]);

  const handleSell = () => {
    const botChainId = token?.tokenId?.startsWith("0x")
      ? BotWalletSupportedChain.ETH
      : BotWalletSupportedChain.SOL;

    const botChainSymbol =
      botChainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";

    if (state.isInstant) {
      mutateInstant(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            amount: token
              ? formatTokenCurrency(state.youSell, token.decimals)
              : formatNativeCurrency(state.youSell, wallet.botChainId),
            slippage: isSellSlippageAuto ? undefined : Number(sellSlippage),
            walletAddress: wallet.address,
          },
          notifyParams: {
            youSell: state.youSell,
            tokenSymbol: token?.symbol,
            youReceive: state.youReceive,
            chainSymbol: botChainSymbol,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [POSITIONS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    } else {
      mutate(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            amount: token
              ? formatTokenCurrency(state.youSell, token.decimals)
              : formatNativeCurrency(state.youSell, wallet.botChainId),
            slippage: isSellSlippageAuto ? undefined : Number(sellSlippage),
            walletAddress: wallet.address,
            triggerPrice: state.triggerPrice,
          },
          notifyParams: {
            youSell: state.youSell,
            tokenSymbol: token?.symbol,
            youReceive: state.youReceive,
            chainSymbol: botChainSymbol,
            limitPrice: state.triggerPrice,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [POSITIONS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    }
  };

  return (
    <GradientBorderRadius className="h-full">
      <div className="h-full w-full p-[0.625rem] pb-[0.313rem] flex flex-col">
        <div className="w-full min-h-[1.45rem] grid grid-cols-[1fr,1fr,1fr] items-center">
          <div className="relative">
            <WalletSelect
              className="w-full text-xs font-semibold"
              signInButtonClassName="w-full text-xs font-semibold"
              selectedWalletType={
                token?.tokenId?.startsWith("0x")
                  ? WalletType.ETH
                  : WalletType.SOL
              }
            />
          </div>

          <div className="text-xs font-semibold flex items-center justify-center">
            Sell
          </div>

          <div className="flex justify-end">
            <MarketLimitButtons
              isInstant={state.isInstant}
              onMarketClick={() => handlers.handleInstantChange(true)}
              onLimitClick={() => handlers.handleInstantChange(false)}
              showBalance={false}
            />
          </div>
        </div>
        {!token?.isLoading ? (
          <>
            <LimitInput
              isInstant={state.isInstant}
              triggerPrice={state.triggerPrice}
              limit={state.limit}
              onLimitChange={handlers.handleLimitChange}
            />
            <BalanceTokenInput
              youSell={state.youSell}
              youSellPercentage={state.youSellPercentage}
              onYouSellChange={handlers.handleYouSellChange}
              onYouSellPercentageChange={handlers.handleYouSellPercentageChange}
            />
            <YouReceiveTokenUsdInput
              youReceive={state.youReceive}
              youReceiveUSD={state.youReceiveUSD}
              onYouReceiveChange={handlers.handleYouReceiveChange}
              onYouReceiveUSDChange={handlers.handleYouReceiveUSDChange}
            />
            <div className="mt-[0.313rem] flex">
              <SlippageToggleInput
                title="Slippage:"
                isAuto={isSellSlippageAuto}
                value={sellSlippage}
                onAutoChange={() => setIsSellSlippageAuto((prev) => !prev)}
                onValueChange={(v) => setSellSlippage(v)}
              />
            </div>
            <div className="mt-[0.313rem] flex justify-center">
              <ConfirmButton
                enabled={
                  isConnected && !isPending && !isPendingInstant && !showSuccess
                }
                onClick={handleSell}
                loading={isPending || isPendingInstant}
                success={showSuccess}
              />
            </div>
          </>
        ) : (
          <div className="h-[13.9rem]">
            <span className="text-xs">Loading...</span>
          </div>
        )}
      </div>
    </GradientBorderRadius>
  );
};

export default SellBotActions;
